import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getFirestore, doc, getDoc, setDoc, updateDoc, arrayUnion, collection, addDoc, serverTimestamp } from 'firebase/firestore';

import './Chat.css';
import SaveChatModal from './SaveChatModal';
import SlideUpDrawer from './SlideUpDrawer';
import ChatPageWrapper from './ChatPageWrapper';
import { debugLog, debugWarn, errorLog } from '../utils/debug';

export default function Chat() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [chatHistory, setChatHistory] = useState([]);
  const [pastConversations, setPastConversations] = useState([]);
  const [pastConversationsLoaded, setPastConversationsLoaded] = useState(false);
  
  // NEW: Memory state for the {{memory}} variable
  const [memory, setMemory] = useState(null);
  const [memoryLoading, setMemoryLoading] = useState(true); // Track memory loading state
  
  // Save Chat Modal and Drawer states
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);
  const [currentSavedChatId, setCurrentSavedChatId] = useState(null);
  const saveModalRef = useRef();
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [currentChatId, setCurrentChatId] = useState(null);
  const chatContentRef = useRef(null);
  const inputRef = useRef(null);
  const db = getFirestore();
  const chatStartTime = useRef(new Date());
  const isMountedRef = useRef(true);

  // Generate UUID for session tracking
  const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  // Initialize session ID on component mount
  useEffect(() => {
    const sessionId = generateUUID();
    setCurrentChatId(sessionId);
    debugLog('🆔 Generated new session ID for Helicone tracking:', sessionId);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const scrollToBottom = () => {
    if (!isMountedRef.current) return;
    
    if (chatContentRef.current && chatContentRef.current.scrollHeight !== undefined) {
      // Use requestAnimationFrame to ensure DOM updates are complete
      requestAnimationFrame(() => {
        if (isMountedRef.current && chatContentRef.current && chatContentRef.current.scrollHeight !== undefined) {
          chatContentRef.current.scrollTop = chatContentRef.current.scrollHeight;
        }
      });
    }
  };

  // Auto-scroll to bottom whenever messages change
  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages]);

  // Load past conversations when component mounts
  useEffect(() => {
    let isMounted = true;
    
    if (currentUser && isMounted) {
      loadPastConversations();
      loadMostRecentMemory(); // NEW: Load most recent memory from Firebase
    }
    
    return () => {
      isMounted = false;
    };
  }, [currentUser]);

  // Monitor memory state changes
  useEffect(() => {
    debugLog('🧠 Memory state changed:', {
      hasValue: !!memory,
      length: memory ? memory.length : 0,
      preview: memory ? memory.substring(0, 100) + '...' : 'None'
    });
  }, [memory]);

  // NEW: Load most recent memory from Firebase
  const loadMostRecentMemory = async () => {
    if (!currentUser) {
      setMemoryLoading(false);
      return;
    }
    
    try {
      debugLog('🧠 Loading most recent memory from Firebase...');
      setMemoryLoading(true);
      
      // Get the user document from Firebase
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const pastConversations = userData.pastConversation || [];
        
        // CRITICAL DEBUG: Log raw Firebase data
        debugLog('🚨 CRITICAL FIREBASE DEBUG:');
        debugLog('🚨 userData keys:', Object.keys(userData));
        debugLog('🚨 pastConversation exists:', !!userData.pastConversation);
        debugLog('🚨 pastConversation length:', pastConversations.length);
        debugLog('🚨 Raw pastConversation data:', pastConversations);
        
        if (pastConversations.length > 0) {
          // Sort by timestamp and get the most recent
          const sortedConversations = pastConversations.sort((a, b) => {
            const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
            const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
            return timeB - timeA; // Most recent first
          });
          
          // Look for memory in new format first (current app)
          let memoryContent = null;
          let formatUsed = null;
          
          debugLog('🔍 Analyzing pastConversation entries for memory:');
          debugLog(`🔍 Total conversations to check: ${sortedConversations.length}`);
          
          for (let i = 0; i < sortedConversations.length; i++) {
            let conversation = sortedConversations[i];
            
            // Handle legacy format: if it's a string, try to parse it as JSON
            if (typeof conversation === 'string') {
              try {
                debugLog(`🔍 Entry ${i} is a string, attempting to parse...`);
                // Try to parse the string as JSON (but it might not be valid JSON)
                // The format looks like: {role: assistant, content: "text", refusal: null, annotations: []}
                // This is not valid JSON, so we need to extract the content manually
                
                // Extract role (should be simple)
                const roleMatch = conversation.match(/role:\s*([^,]+)/);
                
                // Extract content - look for everything between "content: " and ", refusal:"
                // This handles multi-line content with commas and complex text
                const contentMatch = conversation.match(/content:\s*(.*?),\s*refusal:/s);
                
                if (roleMatch && contentMatch) {
                  const role = roleMatch[1].trim();
                  let content = contentMatch[1].trim();
                  
                  // Clean up the content - remove quotes if present
                  if (content.startsWith('"') && content.endsWith('"')) {
                    content = content.slice(1, -1);
                  }
                  
                  conversation = {
                    role: role,
                    content: content,
                    parsedFromString: true
                  };
                  
                  debugLog(`✅ Parsed legacy string entry: role=${role}, content length=${content.length}`);
                } else {
                  debugLog(`❌ Failed to parse legacy string entry: ${conversation.substring(0, 100)}...`);
                  continue;
                }
              } catch (error) {
                debugLog(`❌ Error parsing string entry ${i}:`, error);
                continue;
              }
            }
            
            debugLog(`🔍 Entry ${i}:`, {
              type: conversation.type,
              role: conversation.role,
              hasMemory: !!conversation.memory,
              hasContent: !!conversation.content,
              timestamp: conversation.timestamp,
              keys: Object.keys(conversation),
              parsedFromString: conversation.parsedFromString || false
            });
            
            // Check new format (type: "memory" with memory field)
            if (conversation.type === "memory" && conversation.memory) {
              memoryContent = conversation.memory;
              formatUsed = "new";
              debugLog('✅ Found memory in new format (v1)');
              break;
            }
            // Check legacy format (role: "assistant" with content field)
            else if (conversation.role === "assistant" && conversation.content) {
              memoryContent = conversation.content;
              formatUsed = "legacy";
              debugLog('✅ Found memory in legacy format');
              break;
            }
          }
          
          if (memoryContent) {
            setMemory(memoryContent);
            debugLog(`✅ Most recent memory loaded from Firebase (${formatUsed} format)`);
            debugLog('📄 Memory content:', memoryContent.substring(0, 100) + '...');
            debugLog('📄 Memory length:', memoryContent.length);
          } else {
            debugLog('📭 No memory content found in any format');
            setMemory(null);
          }
        } else {
          debugLog('📭 No past conversations found in user document');
          setMemory(null);
        }
      } else {
        debugLog('📭 User document not found');
        setMemory(null);
      }
      
    } catch (error) {
      errorLog('❌ Error loading memory from Firebase:', error);
      setMemory(null);
    } finally {
      setMemoryLoading(false);
      debugLog('✅ Memory loading completed');
    }
  };

  const loadPastConversations = async () => {
    try {
      if (!currentUser?.uid) {
        debugLog('🔐 No current user, skipping past conversations load');
        return;
      }

      debugLog('📂 Loading past conversations for user:', currentUser.uid);
      
      // Since the backend only stores memory summaries, not raw chat sessions,
      // we'll set an empty array for pastConversations and rely on the memory API
      setPastConversations([]);
      setPastConversationsLoaded(true);
      debugLog('📝 No raw chat sessions stored in backend - using memory system instead');
      
    } catch (error) {
      errorLog('❌ Error loading past conversations:', error);
      // Set empty array as fallback to prevent crashes
      setPastConversations([]);
      setPastConversationsLoaded(true);
    }
  };

  // This function is deprecated - we now write directly to subcollection docs
  // Keeping for potential cleanup/migration purposes
  const saveChatSummaryToFirebase = async (chatId, chatData) => {
    debugLog('⚠️ saveChatSummaryToFirebase called but deprecated - using subcollection instead');
    // No longer writing to chatSummaries array
    return;
  };

  const saveChatToFirebase = async (newChatHistory) => {
    try {
      if (!currentUser?.uid) {
        debugLog('No current user, skipping save to Firebase');
        return;
      }

      if (!newChatHistory || !Array.isArray(newChatHistory) || newChatHistory.length === 0) {
        debugLog('No valid chat history to save');
        return;
      }

      debugLog('📝 Chat session not saved to Firebase - backend handles memory generation automatically');
      debugLog('📝 Chat session contained', newChatHistory.filter(msg => msg && msg.role && msg.content).length, 'messages');
      
    } catch (error) {
      errorLog('Error in saveChatToFirebase:', error);
      // Don't throw error to prevent blocking the chat functionality
    }
  };

  const formatConversationHistory = () => {
    let historyText = '';
    
    debugLog('📝 Formatting conversation history...');
    debugLog('pastConversations:', pastConversations);
    debugLog('chatHistory:', chatHistory);
    
    // Note: pastConversations is now empty since backend only stores memory summaries
    // The actual conversation context comes from the pastConversation memory field
    debugLog('📝 Using memory-based context instead of raw chat sessions');
    
    // Add current chat history (limit to last 15 messages for context)
    if (chatHistory && chatHistory.length > 1) {
      historyText += '\nCurrent conversation:\n';
      debugLog(`Adding current chat history with ${chatHistory.length} messages`);
      
      chatHistory.slice(-15).forEach(msg => {
        if (msg && msg.role && msg.content) {
          historyText += `${msg.role}: ${msg.content}\n`;
        }
      });
    } else {
      debugLog('No current chat history to add');
    }

    debugLog('Final formatted history length:', historyText.length);
    return historyText;
  };

  const formatChatForSummary = () => {
    // Format current chat history as plain text for summarization
    let chatText = '';
    messages.forEach(msg => {
      const role = msg.type === 'ai' ? 'Assistant' : 'User';
      chatText += `${role}: ${msg.content}\n`;
    });
    return chatText;
  };

  const handleInputChange = (e) => {
    if (!e.target) return;
    
    setInputValue(e.target.value);
    // Auto-resize textarea
    e.target.style.height = 'auto';
    if (e.target.scrollHeight) {
      e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px';
    }
  };

  const sendMessage = async () => {
    const message = inputValue.trim();
    if (!message || isLoading || memoryLoading) return;

    // STRICT: Ensure we're using backend, not OpenRouter
    if (process.env.REACT_APP_OPENROUTER_API_KEY || process.env.REACT_APP_OPENROUTER_API_URL) {
      debugWarn('⚠️ OpenRouter configuration detected - this should not be used. Using backend instead.');
    }

    // STRICT: Block any direct OpenRouter calls
    const allowedHosts = ['localhost', '127.0.0.1', '172.17.8.250', 'venty-backend.vercel.app', 'venty-backend-contactmalachy-gmailcoms-projects.vercel.app', 'venty-backend-kevmzi2ui-contactmalachy-gmailcoms-projects.vercel.app', 'venty-backend-8zwai8fdd-contactmalachy-gmailcoms-projects.vercel.app', 'venty-backend-6mqixxrwg-contactmalachy-gmailcoms-projects.vercel.app', 'venty-backend-ca1f7kean-contactmalachy-gmailcoms-projects.vercel.app', 'venty-backend-7uo2wn1bs-contactmalachy-gmailcoms-projects.vercel.app', 'venty-backend-b89ky8415-contactmalachy-gmailcoms-projects.vercel.app', 'venty-backend-jtq4pqfab-contactmalachy-gmailcoms-projects.vercel.app'];
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
    
    try {
      const url = new URL(backendUrl);
      if (!allowedHosts.some(host => url.hostname === host)) {
        errorLog('❌ SECURITY: Only backend calls are allowed');
        throw new Error('Invalid backend URL');
      }
    } catch (urlError) {
      errorLog('❌ Invalid backend URL:', backendUrl);
      return;
    }

    debugLog('✅ VERIFIED: Using backend URL:', backendUrl);

    // Add user message to display
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: message,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    
    // Add to chat history
    const newUserHistoryEntry = { role: 'user', content: message };
    setChatHistory(prev => [...prev, newUserHistoryEntry]);

    setInputValue('');
    setIsLoading(true);

    // Auto-scroll will handle scrolling to bottom

    // Reset textarea height
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }

    // Add typing indicator
    const typingMessage = {
      id: 'typing',
      type: 'typing',
      content: '',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, typingMessage]);

    try {
      // Prepare conversation history for AI
      const conversationContext = formatConversationHistory();
      
      // Debug: Log what's being passed to AI
      debugLog('🧠 AI Memory Context:');
      debugLog('Past Conversations loaded:', pastConversations.length, '(using memory system instead)');
      debugLog('Current Chat History:', chatHistory.length);
      debugLog('Full Context being sent to AI:');
      debugLog(conversationContext);
      
      // Let the backend handle the system prompt - just log memory availability
      debugLog('🧠 Memory loading state:', memoryLoading);
      if (memory && memory.trim()) {
        debugLog('🧠 Memory available, will be included in system prompt');
        debugLog('🧠 Memory length:', memory.length);
        debugLog('🧠 Memory preview:', memory.substring(0, 100) + '...');
      } else {
        debugLog('📭 No memory context available');
      }

      // Debug: Log what's being sent to backend
      debugLog('🚀 Sending to backend API:');
      debugLog('📤 UID:', currentUser.uid);
      debugLog('📤 Message:', message);
      debugLog('📤 Chat History Length:', chatHistory.length);
      debugLog('📤 System Prompt: Using backend default');
      debugLog('📤 Memory Available:', !!memory);
      debugLog('📤 Memory Length:', memory ? memory.length : 0);
      debugLog('📤 Memory Preview:', memory ? memory.substring(0, 100) + '...' : 'None');
      
      // Get backend URL
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      
      // CRITICAL DEBUG: Log all environment variables
      debugLog('🔍 CRITICAL DEBUG - Environment Variables:');
      debugLog('🔍 process.env.REACT_APP_BACKEND_URL:', process.env.REACT_APP_BACKEND_URL);
      debugLog('🔍 BACKEND_URL variable:', BACKEND_URL);
      debugLog('🔍 All REACT_APP_ variables:', Object.keys(process.env).filter(key => key.startsWith('REACT_APP_')));
      
      // Ensure we're using the environment variable, not hardcoded URLs
      if (!process.env.REACT_APP_BACKEND_URL) {
        debugWarn('⚠️ REACT_APP_BACKEND_URL not set, using localhost:8000 as fallback');
      }
      
      debugLog('🚀 Making API call to backend:', `${BACKEND_URL}/api/chat/message`);
      debugLog('🔍 Chat ID for Helicone tracking:', currentChatId);
      
      // Build the actual request payload
      const requestPayload = {
        uid: currentUser.uid,
        chat_id: currentChatId, // Add chat_id for Helicone session tracking
        message: message,
        chat_history: chatHistory,
        system_prompt: null, // Use backend default prompt with proper memory formatting
        memory: memory, // Send memory data for backend to use
        model: process.env.REACT_APP_DEFAULT_MODEL || 'anthropic/claude-3.5-haiku',
        temperature: 1.0,
        max_tokens: 250
      };

      // CRITICAL DEBUG: Log memory being sent to backend
      debugLog('🚨 CRITICAL MEMORY DEBUG - Frontend to Backend:');
      debugLog('🚨 memory value:', memory);
      debugLog('🚨 memory type:', typeof memory);
      debugLog('🚨 memory is null:', memory === null);
      debugLog('🚨 memory is undefined:', memory === undefined);
      debugLog('🚨 memory length:', memory ? memory.length : 'N/A');
      debugLog('🚨 requestPayload.memory:', requestPayload.memory);
      
      if (process.env.REACT_APP_DEBUG_MODE === 'true') {
        debugLog('📤 Actual request payload:', {
        uid: requestPayload.uid,
        message: requestPayload.message,
        chat_history_length: requestPayload.chat_history.length,
        system_prompt: requestPayload.system_prompt,
        memory: requestPayload.memory, // Show actual memory field being sent
        memory_available: !!requestPayload.memory,
        memory_length: requestPayload.memory ? requestPayload.memory.length : 0,
        model: requestPayload.model,
        temperature: requestPayload.temperature,
        max_tokens: requestPayload.max_tokens
        });
      }
      
      const response = await fetch(`${BACKEND_URL}/api/chat/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestPayload)
      });

      debugLog('📥 Backend response status:', response.status);
      debugLog('📥 Backend response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        throw new Error(`Backend API request failed: ${response.status}`);
      }

      const data = await response.json();
      const aiResponse = data.response || 'sorry, having trouble processing that. could you try again?';

      // Remove typing indicator and add AI response to display
      setMessages(prev => {
        const withoutTyping = prev.filter(msg => msg.id !== 'typing');
        return [...withoutTyping, {
          id: Date.now() + 1,
          type: 'ai',
          content: aiResponse,
          timestamp: new Date()
        }];
      });

      // Add AI response to chat history (newUserHistoryEntry already added earlier)
      const newAIHistoryEntry = { role: 'assistant', content: aiResponse };
      const updatedChatHistory = [...chatHistory, newUserHistoryEntry, newAIHistoryEntry];
      setChatHistory(updatedChatHistory);

      // Scroll to bottom after AI response is added
      scrollToBottom();

      // Note: We no longer save raw chat history to Firebase during conversation
      // This will be handled by the backend summarization process when user clicks "Exit and save"

    } catch (error) {
      errorLog('Error calling backend chat API:', error);
      
      // Remove typing indicator and add error message
      setMessages(prev => {
        const withoutTyping = prev.filter(msg => msg.id !== 'typing');
        return [...withoutTyping, {
          id: Date.now() + 1,
          type: 'ai',
          content: 'having some connection trouble right now. give me a sec and try again?',
          timestamp: new Date()
        }];
      });

      // Scroll to bottom after error message is added
      scrollToBottom();
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleExitAndSave = async () => {
    try {
      setIsSaving(true);
      
      if (!currentUser) {
        alert('Please sign in to save chat.');
        return;
      }

      if (chatHistory.length <= 1) { // Only initial AI message
        alert('No chat content to save.');
        return;
      }

      debugLog('💾 Creating new chat document...');

      // Try subcollection first, fallback to user document if permission error
      let chatDocRef;
      let chatId;
      
      try {
        // A) Create minimal chat doc in subcollection - NO TRANSCRIPT
        const userChatsRef = collection(db, 'users', currentUser.uid, 'chats');
        const chatData = {
          status: 'pending',
          endedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
          // NO chatHistory, NO transcript - only summary will be added later
        };

        chatDocRef = await addDoc(userChatsRef, chatData);
        chatId = chatDocRef.id;
        debugLog('✅ Minimal chat document created in subcollection with ID:', chatId);

      } catch (permissionError) {
        errorLog('❌ Subcollection permission error, using fallback method:', permissionError);
        
        // Fallback: Create minimal pending entry in user document - NO TRANSCRIPT
        chatId = `chat_${Date.now()}`;
        const userDocRef = doc(db, 'users', currentUser.uid);
        
        const fallbackChatData = {
          id: String(chatId),
          status: 'pending',
          timestamp: new Date().toISOString(),
          endedAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
          // NO chatHistory, NO transcript - only summary will be added later
        };

        try {
          // Check if user document exists first
          const userDocSnap = await getDoc(userDocRef);
          if (!userDocSnap.exists()) {
            // Create user document if it doesn't exist
            await setDoc(userDocRef, {
              tempChats: [fallbackChatData],
              createdAt: new Date().toISOString()
            });
            debugLog('✅ Created new user document with minimal chat data');
          } else {
            // Update existing user document
            await updateDoc(userDocRef, {
              tempChats: arrayUnion(fallbackChatData)
            });
            debugLog('✅ Minimal chat data saved to user document fallback:', chatId);
          }
        } catch (fallbackError) {
          errorLog('❌ Fallback method also failed:', fallbackError);
          alert('Unable to save chat. Please check your internet connection and try again.');
          return;
        }
      }

      // Store chat ID and show loading modal
      setCurrentSavedChatId(chatId);
      setShowSaveModal(true);

      // Format chat for summary
      const chatText = formatChatForSummary();
      
      // Call backend to process the summary
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      
      debugLog('🚀 Starting chat summarization process...');
      
      const summaryResponse = await fetch(`${BACKEND_URL}/api/chats/summarize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          uid: currentUser.uid,
          chat_id: chatId,
          session_id: currentChatId, // Add session ID for Helicone tracking
          chat_text: chatText,
          lang: 'en',
          started_at: chatStartTime.current.toISOString(),
          ended_at: new Date().toISOString()
        })
      });

      if (summaryResponse.ok) {
        const result = await summaryResponse.json();
        debugLog('✅ Chat summary started successfully:', result);
        
        // Generate memory separately after summary is created
        try {
          debugLog('🧠 Calling separate memory generation API...');
          
          // Get the most recent memory from Firebase
          let previousMemory = null;
          try {
            const userDocRef = doc(db, 'users', currentUser.uid);
            const userDoc = await getDoc(userDocRef);
            
            if (userDoc.exists()) {
              const userData = userDoc.data();
              const pastConversations = userData.pastConversation || [];
              
              if (pastConversations.length > 0) {
                // Sort by timestamp and get the most recent
                const sortedConversations = pastConversations.sort((a, b) => {
                  const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
                  const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
                  return timeB - timeA; // Most recent first
                });
                
                const mostRecent = sortedConversations[0];
                if (mostRecent && mostRecent.memory) {
                  previousMemory = mostRecent.memory;
                  debugLog('🧠 Found previous memory:', previousMemory.substring(0, 100) + '...');
                }
              }
            }
          } catch (error) {
            console.warn('Could not load previous memory:', error);
          }
          
          const memoryResponse = await fetch(`${BACKEND_URL}/api/users/${currentUser.uid}/memory/generate`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              uid: currentUser.uid,
              chat_id: chatId,
              session_id: currentChatId, // Add session ID for Helicone tracking
              chat_text: chatText,
              previous_memory: previousMemory,
              lang: 'en'
            })
          });

          if (memoryResponse.ok) {
            const memoryResult = await memoryResponse.json();
            debugLog('✅ Memory generated successfully:', memoryResult);
            
            // Save the generated memory to Firebase pastConversation array
            if (memoryResult.memory_data && memoryResult.memory_data.memory) {
              try {
                const userDocRef = doc(db, 'users', currentUser.uid);
                
                const memory_entry = {
                  id: chatId,
                  type: "memory",
                  memory: memoryResult.memory_data.memory,
                  timestamp: new Date().toISOString(),
                  version: "v1"
                };
                
                // Use arrayUnion to add the memory entry to pastConversation array
                await updateDoc(userDocRef, {
                  pastConversation: arrayUnion(memory_entry)
                });
                
                debugLog('✅ Memory saved to Firebase successfully');
                
                // Reload memory immediately for next conversation
                await loadMostRecentMemory();
                
              } catch (error) {
                // If user document doesn't exist, create it with the memory
                try {
                  await setDoc(userDocRef, {
                    pastConversation: [memory_entry]
                  });
                  await loadMostRecentMemory();
                } catch (setError) {
                  console.error('Failed to save memory to Firebase:', setError);
                }
              }
            }
          } else {
            console.error('Memory generation failed:', await memoryResponse.text());
          }
        } catch (memoryError) {
          console.error('Error calling memory generation API:', memoryError);
        }
        
        // Start polling for completed summary
        const pollingSummaryId = setInterval(async () => {
          try {
            const completedResponse = await fetch(`${BACKEND_URL}/api/users/${currentUser.uid}/chats/${chatId}/summary`);

            if (completedResponse.ok) {
              const completedData = await completedResponse.json();
              if (completedData.found) {
                debugLog('✅ Summary completed, updating Firestore...');
                clearInterval(pollingSummaryId);
                
                // Update the Firestore document with the completed summary
                try {
                  const userChatsRef = collection(db, 'users', currentUser.uid, 'chats');
                  const chatDocRef = doc(userChatsRef, chatId);
                  
                  await updateDoc(chatDocRef, {
                    status: completedData.summary_data.status,
                    summary: completedData.summary_data.summary,
                    meta: completedData.summary_data.meta,
                    updatedAt: new Date()
                  });
                  
                  debugLog('✅ Firestore updated with completed summary');
                  
                  // Note: Memory is now loaded directly from Firebase user document
                  // No need to call backend memory API
                  debugLog('🧠 Memory will be loaded from Firebase user document on next page load');
                  
                  // Show success state and navigate to session summary
                  if (saveModalRef.current) {
                    saveModalRef.current.showSuccessState();
                  }
                  
                } catch (firestoreError) {
                  errorLog('❌ Failed to update Firestore with summary:', firestoreError);
                }
              }
            }
          } catch (pollError) {
            errorLog('❌ Polling error:', pollError);
          }
        }, 2000); // Poll every 2 seconds
        
        // Clear polling after 30 seconds to prevent infinite polling
        setTimeout(() => {
          clearInterval(pollingSummaryId);
          debugLog('⏰ Polling timeout reached');
        }, 30000);
        
      } else {
        const errorData = await summaryResponse.json().catch(() => ({}));
        errorLog('❌ Chat summarization failed:', errorData);
        alert(`Failed to start chat summarization: ${errorData.detail || 'Unknown error'}`);
      }
      
    } catch (error) {
      errorLog('❌ Error saving chat:', error);
      alert('Failed to save chat. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle success callback from SaveChatModal
  const handleSaveSuccess = (chatId) => {
    debugLog('🎉 Save success callback triggered for:', chatId);
    
    // Clear the chat immediately
    setChatHistory([]);
    setMessages([]);
    
    // Close loading modal and navigate to session summary
    setShowSaveModal(false);
    navigate(`/sessionsummary/${chatId}`);
  };

  const handleCloseDrawer = () => {
    setShowDrawer(false);
    // Navigate to chats page after closing drawer
    setTimeout(() => {
      navigate('/chats');
    }, 300); // Small delay for smooth transition
  };

  const TypingIndicator = () => (
    <div className="message ai">
      <div className="message-bubble">
        <div className="typing-indicator">
          <div className="typing-dot"></div>
          <div className="typing-dot"></div>
          <div className="typing-dot"></div>
        </div>
      </div>
    </div>
  );

  return (
    <ChatPageWrapper className="main-container">
      {/* Chat Header - Fixed to top */}
      <div className="chat-header">
        <div className="header-content">
          <button 
            className="exit-save-btn" 
            onClick={handleExitAndSave}
            disabled={isSaving}
          >
            {isSaving ? '💾 Saving...' : '💾 Save Chat'}
          </button>

          <button 
            className="logout-btn" 
            onClick={() => {
              if (window.confirm('Are you sure you want to exit without saving? If you would like to save your chat, please press the "Save Chat" button.')) {
                navigate('/');
              }
            }}
            disabled={isSaving}
          >
            🚪 Exit
          </button>
        </div>
      </div>
      
      {/* Chat Content - Scrollable area */}
      <div className="chat-content scrollable" ref={chatContentRef}>
        {messages.map((message) => {
          if (message.type === 'typing') {
            return <TypingIndicator key={message.id} />;
          }
          
          return (
            <div key={message.id} className={`message ${message.type}`}>
              <div className="message-bubble">
                {message.content}
              </div>
            </div>
          );
        })}
      </div>

      {/* Chat Input - Fixed to bottom */}
      <div className="chat-input-container">
        <div className="input-wrapper">
          <textarea
            ref={inputRef}
            className="chat-input"
            placeholder={memoryLoading ? "loading your past context..." : "i'm feeling..."}
            rows="1"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            disabled={isLoading || isSaving || memoryLoading}
          />
          <button 
            className="send-btn" 
            onClick={sendMessage}
            disabled={isLoading || !inputValue.trim() || isSaving || memoryLoading}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m5 12 7-7 7 7"/>
              <path d="M12 19V5"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Save Chat Loading Modal */}
      <SaveChatModal
        ref={saveModalRef}
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        onSuccess={handleSaveSuccess}
        chatId={currentSavedChatId}
        chatTitle="Your chat is being saved"
      />

      {/* Slide-up Session Summary Drawer */}
      <SlideUpDrawer
        isOpen={showDrawer}
        onClose={handleCloseDrawer}
        chatId={currentSavedChatId}
      />
    </ChatPageWrapper>
  );
}
